import { z } from 'zod';
import {
  BigDecimalString,
  CurrencyCode,
  IsoDateString,
} from '@/schemas/deals/shared';

export const InstrumentSchema = z.object({
  id: z.string(),
  instrument_type: z.string(),
  group: z.string(),
  name: z.string(),
  currency: z.string(),
  maturity_date: z.string().nullable(),
  status: z.string(),
  details: z.record(z.string(), z.unknown()),
  extensions: z.record(z.string(), z.unknown()),
  created_at: z.string(),
  updated_at: z.string(),
});

export const InstrumentEventSchema = z.object({
  id: z.string(),
  instrument_id: z.string(),
  event_date: z.string(),
  event_type: z.string(),
  details: z.record(z.string(), z.unknown()),
  extensions: z.record(z.string(), z.unknown()),
  recorded_at: z.string(),
});

const RequiredText = z.string().trim().min(1, 'required');
const OptionalText = z.string().trim().optional();
const ContractMonthSchema = z
  .string()
  .trim()
  .regex(/^[A-Z][0-9]{2}$/, 'contractMonthFormat');
const OptionalIsinSchema = z
  .string()
  .trim()
  .optional()
  .refine(
    (value) => value == null || value.length === 0 || /^[A-Z]{2}[A-Z0-9]{10}$/.test(value),
    'invalidIsin',
  );

export const SupportedInstrumentTypeSchema = z.enum([
  'bond',
  'fx_pair',
  'futures_contract',
]);

export const BondCouponFrequencySchema = z.enum([
  'monthly',
  'quarterly',
  'semi_annual',
  'annual',
  'zero_coupon',
  'at_maturity',
]);

export const BondDayCountConventionSchema = z.enum([
  'ACT_360',
  'ACT_365',
  'ACT_365F',
  'ACT_ACT_ISDA',
  'ACT_ACT_ICMA',
  '30_360',
  '30E_360',
]);

export const QuoteConventionSchema = z.enum([
  'direct',
  'indirect',
]);

export const FuturesAssetClassSchema = z.enum([
  'rates',
  'fx',
  'equity',
  'commodity',
  'credit',
]);

export const FuturesSettlementTypeSchema = z.enum([
  'physical',
  'cash',
]);

// ---------------------------------------------------------------------------
// FRN / coupon-type primitives (relocated from schemas/deals/bond.ts).
//
// These describe an INSTRUMENT'S coupon definition, not a trade. They mirror
// the backend bond instrument JSON Schema (schemas/instruments/bond.json),
// where float_* numeric terms are JSON `number` and the RFR convention block is
// required when float_index is an RFR index.
// ---------------------------------------------------------------------------

/** UI-only: drives progressive disclosure. Backend derives subtype from float_index presence. */
export const CouponType = z.enum(['fixed', 'floating', 'zero', 'inflation_linked']);
export type CouponType = z.infer<typeof CouponType>;

/** Risk-free reference rate indices. Market-agnostic selectable data values. */
export const RFR_FLOAT_INDEXES = ['CORRA', 'SOFR', 'SONIA', 'ESTR', 'TONA', 'SARON'] as const;
export type RfrFloatIndex = (typeof RFR_FLOAT_INDEXES)[number];

export function isRfrFloatIndex(value: string): value is RfrFloatIndex {
  return (RFR_FLOAT_INDEXES as readonly string[]).includes(value.toUpperCase());
}

export const FloatCompounding = z.enum([
  'none',
  'compounded_daily',
  'compounded_period',
  'flat',
]);
export type FloatCompounding = z.infer<typeof FloatCompounding>;

export const FloatResetFrequency = z.enum([
  'monthly',
  'quarterly',
  'semi_annual',
  'annual',
]);
export type FloatResetFrequency = z.infer<typeof FloatResetFrequency>;

export const BondDetailsSchema = z.object({
  face_value: z.number().positive('mustBePositive'),
  currency: CurrencyCode,
  issue_date: IsoDateString,
  maturity_date: IsoDateString,
  issuer: z.object({
    name: RequiredText,
  }),
  coupon_rate: z.number().nonnegative('mustBePositiveOrZero'),
  coupon_frequency: BondCouponFrequencySchema,
  day_count_convention: BondDayCountConventionSchema,
  isin: OptionalText,
  // FRN / floating-rate convention block. Mirrors backend bond.json, where
  // these numeric terms are JSON `number` (not BigDecimal strings).
  float_index: OptionalText,
  float_spread: z.number().optional(),
  float_reset_frequency: FloatResetFrequency.optional(),
  float_compounding: FloatCompounding.optional(),
  lookback_days: z.number().int().min(0).optional(),
  observation_shift: z.boolean().optional(),
  payment_delay_days: z.number().int().min(0).optional(),
  rate_cutoff_days: z.number().int().min(0).optional(),
  fixing_calendar: OptionalText,
  float_cap: z.number().optional(),
  float_floor: z.number().optional(),
  // Inflation-linked.
  inflation_index: OptionalText,
});

export const FxPairDetailsSchema = z.object({
  base_currency: CurrencyCode,
  quote_currency: CurrencyCode,
  spot_settlement_days: z.number().int().min(0).max(3),
  quote_convention: QuoteConventionSchema,
});

export const FuturesDetailsSchema = z.object({
  exchange: RequiredText,
  contract_code: RequiredText,
  contract_month: ContractMonthSchema,
  expiry_date: IsoDateString,
  contract_size: z.number().positive('mustBePositive'),
  currency: CurrencyCode,
  tick_size: z.number().positive('mustBePositive'),
  tick_value: z.number().positive('mustBePositive'),
  asset_class: FuturesAssetClassSchema,
  settlement_type: FuturesSettlementTypeSchema.optional(),
  underlying_description: OptionalText,
});

const CreateInstrumentBaseSchema = z.object({
  name: RequiredText,
  currency: CurrencyCode,
  identifiers: z.record(z.string(), z.string()).optional(),
  extensions: z.record(z.string(), z.unknown()).optional(),
});

const BondCreateInstrumentSchema = CreateInstrumentBaseSchema.extend({
  instrument_type: z.literal('bond'),
  maturity_date: IsoDateString,
  details: BondDetailsSchema,
});

export const CreateInstrumentSchema = z.discriminatedUnion('instrument_type', [
  BondCreateInstrumentSchema,
  CreateInstrumentBaseSchema.extend({
    instrument_type: z.literal('fx_pair'),
    details: FxPairDetailsSchema,
  }),
  CreateInstrumentBaseSchema.extend({
    instrument_type: z.literal('futures_contract'),
    maturity_date: IsoDateString,
    details: FuturesDetailsSchema,
  }),
]).superRefine((value, ctx) => {
  if (
    value.instrument_type === 'bond' &&
    value.maturity_date !== value.details.maturity_date
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['details', 'maturity_date'],
      message: 'bondMaturityMustMatchTopLevel',
    });
  }
});

export const InstrumentManagerIdentifiersSchema = z.object({
  isin: OptionalIsinSchema,
  contract_code: OptionalText,
});

export const InstrumentManagerDetailsSchema = z.object({
  face_value: OptionalText,
  issue_date: OptionalText,
  coupon_rate: OptionalText,
  coupon_frequency: BondCouponFrequencySchema.optional(),
  day_count_convention: BondDayCountConventionSchema.optional(),
  issuer_name: OptionalText,
  // Coupon-type controller (UI progressive disclosure). Backend derives subtype
  // from float_index / inflation_index presence.
  coupon_type: CouponType.optional(),
  // FRN / RFR convention block — string-typed at the form boundary, coerced to
  // number when building the instrument create request.
  float_index: OptionalText,
  float_spread: OptionalText,
  float_reset_frequency: FloatResetFrequency.optional(),
  float_compounding: FloatCompounding.optional(),
  lookback_days: OptionalText,
  observation_shift: z.boolean().optional(),
  payment_delay_days: OptionalText,
  rate_cutoff_days: OptionalText,
  fixing_calendar: OptionalText,
  float_cap: OptionalText,
  float_floor: OptionalText,
  // Inflation-linked.
  inflation_index: OptionalText,
  base_currency: OptionalText,
  quote_currency: OptionalText,
  spot_settlement_days: z.number().int().min(0).max(3).optional(),
  quote_convention: QuoteConventionSchema.optional(),
  exchange: OptionalText,
  contract_month: OptionalText,
  expiry_date: OptionalText,
  contract_size: OptionalText,
  tick_size: OptionalText,
  tick_value: OptionalText,
  asset_class: FuturesAssetClassSchema.optional(),
  settlement_type: FuturesSettlementTypeSchema.optional(),
  underlying_description: OptionalText,
});

export const InstrumentManagerFormSchema = z
  .object({
    instrument_type: SupportedInstrumentTypeSchema,
    name: RequiredText,
    currency: CurrencyCode,
    maturity_date: OptionalText,
    identifiers: InstrumentManagerIdentifiersSchema,
    details: InstrumentManagerDetailsSchema,
    /**
     * Raw JSON string for the extensions editor (amend mode only).
     * Held as a string so RHF can manage it without coercion.
     * Validated separately: must be valid JSON encoding a plain object when present.
     */
    extensions_json: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    const requireField = (
      condition: boolean,
      path: Array<string>,
      fieldValue: string | number | undefined,
    ) => {
      const isMissing =
        fieldValue == null ||
        (typeof fieldValue === 'string' && fieldValue.trim().length === 0);
      if (condition && isMissing) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: 'required',
        });
      }
    };

    const requireDecimal = (
      condition: boolean,
      path: Array<string>,
      fieldValue: string | undefined,
      options?: { allowZero?: boolean },
    ) => {
      requireField(condition, path, fieldValue);
      if (!condition || !fieldValue || fieldValue.trim().length === 0) {
        return;
      }
      const parsed = BigDecimalString.safeParse(fieldValue);
      if (!parsed.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: 'invalidDecimal',
        });
        return;
      }
      const parsedNumber = Number(fieldValue);
      const allowZero = options?.allowZero ?? false;
      const isInvalid = allowZero ? parsedNumber < 0 : parsedNumber <= 0;
      if (isInvalid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: allowZero ? 'mustBePositiveOrZero' : 'mustBePositive',
        });
      }
    };

    const requireDate = (
      condition: boolean,
      path: Array<string>,
      fieldValue: string | undefined,
    ) => {
      requireField(condition, path, fieldValue);
      if (!condition || !fieldValue || fieldValue.trim().length === 0) {
        return;
      }
      const parsed = IsoDateString.safeParse(fieldValue);
      if (!parsed.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: 'dateFormat',
        });
      }
    };

    const requireCurrency = (
      condition: boolean,
      path: Array<string>,
      fieldValue: string | undefined,
    ) => {
      requireField(condition, path, fieldValue);
      if (!condition || !fieldValue || fieldValue.trim().length === 0) {
        return;
      }
      const parsed = CurrencyCode.safeParse(fieldValue);
      if (!parsed.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: 'currency3',
        });
      }
    };

    const isSharable =
      value.instrument_type === 'bond' || value.instrument_type === 'futures_contract';

    requireDate(isSharable, ['maturity_date'], value.maturity_date);

    if (value.instrument_type === 'bond') {
      const couponType = value.details.coupon_type ?? 'fixed';
      requireDecimal(true, ['details', 'face_value'], value.details.face_value);
      requireDate(true, ['details', 'issue_date'], value.details.issue_date);
      // Zero-coupon bonds carry no coupon rate; everything else requires one.
      requireDecimal(
        couponType !== 'zero',
        ['details', 'coupon_rate'],
        value.details.coupon_rate,
        { allowZero: true },
      );
      requireField(true, ['details', 'coupon_frequency'], value.details.coupon_frequency);
      requireField(
        true,
        ['details', 'day_count_convention'],
        value.details.day_count_convention,
      );
      requireField(true, ['details', 'issuer_name'], value.details.issuer_name);

      // Floating-rate notes referencing an RFR index require the full RFR
      // convention block (mirrors backend bond.json allOf rule).
      const floatIndex = value.details.float_index?.trim() ?? '';
      if (couponType === 'floating') {
        requireField(true, ['details', 'float_index'], value.details.float_index);
        if (floatIndex.length > 0 && isRfrFloatIndex(floatIndex)) {
          requireField(true, ['details', 'float_compounding'], value.details.float_compounding);
          requireField(true, ['details', 'lookback_days'], value.details.lookback_days);
          requireField(true, ['details', 'payment_delay_days'], value.details.payment_delay_days);
          requireField(true, ['details', 'rate_cutoff_days'], value.details.rate_cutoff_days);
          requireField(true, ['details', 'fixing_calendar'], value.details.fixing_calendar);
        }
      }

      // Inflation-linked bonds require an inflation index.
      if (couponType === 'inflation_linked') {
        requireField(true, ['details', 'inflation_index'], value.details.inflation_index);
      }
    }

    if (value.instrument_type === 'fx_pair') {
      requireCurrency(true, ['details', 'base_currency'], value.details.base_currency);
      requireCurrency(true, ['details', 'quote_currency'], value.details.quote_currency);
      requireField(
        true,
        ['details', 'spot_settlement_days'],
        value.details.spot_settlement_days,
      );
      requireField(
        true,
        ['details', 'quote_convention'],
        value.details.quote_convention,
      );
    }

    if (value.instrument_type === 'futures_contract') {
      requireField(true, ['identifiers', 'contract_code'], value.identifiers.contract_code);
      if (
        value.identifiers.contract_code &&
        value.identifiers.contract_code.trim().length > 0 &&
        value.identifiers.contract_code !== value.identifiers.contract_code.toUpperCase()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identifiers', 'contract_code'],
          message: 'contractCodeUppercase',
        });
      }
      requireField(true, ['details', 'exchange'], value.details.exchange);
      requireField(true, ['details', 'contract_month'], value.details.contract_month);
      if (
        value.details.contract_month &&
        value.details.contract_month.trim().length > 0 &&
        !ContractMonthSchema.safeParse(value.details.contract_month).success
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['details', 'contract_month'],
          message: 'contractMonthFormat',
        });
      }
      requireDate(true, ['details', 'expiry_date'], value.details.expiry_date);
      requireDecimal(true, ['details', 'contract_size'], value.details.contract_size);
      requireDecimal(true, ['details', 'tick_size'], value.details.tick_size);
      requireDecimal(true, ['details', 'tick_value'], value.details.tick_value);
      requireField(true, ['details', 'asset_class'], value.details.asset_class);
    }
  });

export const InstrumentManagerPayloadSchema = z.object({
  instrument_type: SupportedInstrumentTypeSchema.optional(),
  name: OptionalText,
  currency: OptionalText,
  maturity_date: OptionalText,
  identifiers: InstrumentManagerIdentifiersSchema.partial().optional(),
  details: InstrumentManagerDetailsSchema.partial().optional(),
});

export const InstrumentDetailPayloadSchema = z.object({
  instrumentId: z.string().min(1).optional(),
});

export const InstrumentEventsPayloadSchema = z.object({
  instrumentId: z.string().min(1).optional(),
  instrumentType: z.string().min(1).optional(),
  instrumentName: z.string().min(1).optional(),
  intent: z.enum(['add_event']).optional(),
});

export const AddEventFormSchema = z.object({
  event_type: z.string().trim().min(1, 'required'),
  event_date: IsoDateString,
  details: z.string().trim().optional(),
});

export const CorrectEventFormSchema = z.object({
  event_date: IsoDateString,
  details: z.string().trim().optional(),
});

export const ReverseEventFormSchema = z.object({
  reversal_date: IsoDateString,
  reason: z.string().trim().min(1, 'required'),
});

export const SupersedeEventFormSchema = z.object({
  event_type: z.string().trim().min(1, 'required'),
  event_date: IsoDateString,
  details: z.string().trim().optional(),
});

export type Instrument = z.infer<typeof InstrumentSchema>;
export type InstrumentEvent = z.infer<typeof InstrumentEventSchema>;
export type SupportedInstrumentType = z.infer<typeof SupportedInstrumentTypeSchema>;
export type CreateInstrumentRequest = z.infer<typeof CreateInstrumentSchema>;
export type InstrumentManagerForm = z.infer<typeof InstrumentManagerFormSchema>;
export type InstrumentManagerPayload = z.infer<typeof InstrumentManagerPayloadSchema>;
export type InstrumentDetailPayload = z.infer<typeof InstrumentDetailPayloadSchema>;
export type InstrumentEventsPayload = z.infer<typeof InstrumentEventsPayloadSchema>;
export type AddEventForm = z.infer<typeof AddEventFormSchema>;
export type CorrectEventForm = z.infer<typeof CorrectEventFormSchema>;
export type ReverseEventForm = z.infer<typeof ReverseEventFormSchema>;
export type SupersedeEventForm = z.infer<typeof SupersedeEventFormSchema>;
// Input types for RHF (before Zod transforms — used by useForm<T>)
export type AddEventFormInput = z.input<typeof AddEventFormSchema>;
export type CorrectEventFormInput = z.input<typeof CorrectEventFormSchema>;
export type SupersedeEventFormInput = z.input<typeof SupersedeEventFormSchema>;
